import { Card, Spacer, TextCaption } from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';

import type { OverriddenBy } from './overridden-in';
import './maestro-org.scss';

const key = (name: string) => `manager/organizations:orgTabs.${name}`;

function OverriddenByCell({ row }: { row: OverriddenBy }) {
  const { t } = useTranslation();
  if (row.scopeType === 'application') {
    return (
      <>
        {t(key('overriddenIn.application'))}
        <TextCaption className="c-org-sub">
          {t(key('overriddenIn.inProject'), { name: row.name ?? '' })}
        </TextCaption>
      </>
    );
  }
  return (
    <>
      {row.name ?? '—'}
      <TextCaption className="c-org-sub">
        {t(key(`scopes.${row.scopeType}`))}
      </TextCaption>
    </>
  );
}

/** Every level below the org that sets its own value for this tab's groups. */
export function OverriddenInTable({
  org,
  rows,
}: {
  org: string;
  rows: OverriddenBy[];
}) {
  const { t } = useTranslation();
  return (
    <Card
      size="large"
      shadow="none"
      border="default"
      test-id="org-overridden-in"
    >
      <h2 className="c-org-card-title">{t(key('overriddenIn.title'))}</h2>
      <TextCaption variant="secondary">
        {t(key('overriddenIn.lede'), { org })}
      </TextCaption>
      <Spacer size="medium" />
      {rows.length === 0 ? (
        <TextCaption variant="secondary" test-id="org-overridden-in-empty">
          {t(key('overriddenIn.empty'))}
        </TextCaption>
      ) : (
        <div className="c-org-frame">
          <table className="c-org-table">
            <thead>
              <tr>
                <th scope="col">{t(key('overriddenIn.columns.by'))}</th>
                <th scope="col">{t(key('overriddenIn.columns.setting'))}</th>
                <th scope="col">{t(key('overriddenIn.columns.value'))}</th>
                <th scope="col">{t(key('overriddenIn.columns.orgDefault'))}</th>
              </tr>
            </thead>
            {rows.map((row) => (
              <tbody key={row.key} data-test-id="org-overridden-row">
                {row.settings.map((setting, index) => (
                  <tr key={setting.key}>
                    {index === 0 && (
                      <th scope="rowgroup" rowSpan={row.settings.length}>
                        <OverriddenByCell row={row} />
                      </th>
                    )}
                    <td>{setting.label}</td>
                    <td>
                      {setting.same ? (
                        setting.value
                      ) : (
                        <code>{setting.value}</code>
                      )}
                    </td>
                    <td className="c-org-muted">
                      {setting.orgDefault && <code>{setting.orgDefault}</code>}
                    </td>
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        </div>
      )}
    </Card>
  );
}
